import {
  formatPhoneNumber,
  IORGDataTableQueryState,
} from 'shared-it-appmod-ui';
import { set, unset } from 'lodash';
import { IRequest, IRequestForm, IRequestItem } from 'src/models/request.model';
import {
  AccessType,
  FlightCategory,
  RequestFlightPathName,
  RequestListTab,
  RequestStatus,
  AccessRole,
} from 'src/constants';
import Lang from 'src/libraries/language';
import { IRequestState, actionTypes } from 'src/ducks/request.duck';
import { IStatus } from 'src/libraries/thunk.library';
import { PartialDeep } from 'type-fest';
import { getUrl } from 'src/libraries/common.library';
import { getIsAdmin, getUserType } from 'src/libraries/access.library';
import { getUser } from 'src/libraries/auth.library';
import Moment from 'src/libraries/moment.library';
import { IChangeLog } from 'src/models/change-log.model';

export const isRequestAccessible = (
  request: {
    requestId?: number;
    requestStatus?: string | null;
    requestorNo?: string | null;
  },
  type?: AccessType
) => {
  const isAdmin = getIsAdmin();
  const isSuperAdmin = getUserType() === AccessRole.SUPER_ADMIN;
  const user = getUser();
  let hasAccess = true;

  // Prevent all access if the request is already cancelled
  if (request.requestStatus === RequestStatus.Cancelled && !isSuperAdmin) {
    hasAccess = false;
  }

  if (
    type === AccessType.FLIGHT_REQUEST_CANCEL &&
    !isAdmin &&
    request.requestorNo !== user?.emp_no
  ) {
    return false;
  }

  if (
    type === AccessType.FLIGHT_REQUEST_UPLOAD &&
    !isAdmin &&
    request.requestorNo !== user?.emp_no
  ) {
    return false;
  }

  if (type === AccessType.FLIGHT_REQUEST_UPDATE_COST && !isAdmin) {
    return false;
  }

  if (
    type === AccessType.FLIGHT_REQUEST_RISK_ASSESSMENT &&
    !isAdmin &&
    request.requestorNo !== user?.emp_no
  ) {
    return false;
  }

  if (
    !isSuperAdmin &&
    type === AccessType.FLIGHT_REQUEST_DUPLICATE &&
    request.requestorNo !== user?.emp_no
  ) {
    hasAccess = false;
  }

  return hasAccess;
};

export const getRequestStatus = (
  state: IRequestState,
  action: keyof typeof actionTypes
): IStatus =>
  state.status[action] ?? {
    fetching: false,
    error: null,
  };

export const countCategory = (category: string, data?: IRequestItem[]) => {
  return data?.map(
    (request) =>
      request.flightCategory?.includes(category) &&
      request.requestStatus !== Lang.LBL_CANCELLED
  ).length;
};

export const getTableListParams = (
  params: IORGDataTableQueryState,
  currentTab?: RequestListTab,
  currentPath?: string,
): Partial<IORGDataTableQueryState> => {
  const { keyword, ...temp } = params;
  let data = temp;

  if (keyword) {
    data.filters = [
      ...(data.filters ?? []),
      {
        name: 'requestId',
        value: keyword,
      },
    ];
  }

  // Only admin users have ability to use Today's Flight option
  if (!data.flightRange && getIsAdmin() && currentPath !== '/flightsCalendar') {
    if (!data.flightDate || (!data.flightDateStart && !data.flightDateEnd)) {
      data.flightDate = Moment().format('MM/DD/YYYY');
    }
  }

  if (data.flightRange) {
    switch (data.flightRange) {
      case 'todaysFlights': {
        data = {
          ...data,
          flightDate: Moment().format('MM/DD/YYYY'),
          flightDateStart: undefined,
          flightDateEnd: undefined,
        };

        break;
      }

      case 'last30DaysFlights': {
        data = {
          ...data,
          flightDate: undefined,
          flightDateStart: Moment().subtract(30, 'days').format('MM/DD/YYYY'),
          flightDateEnd: Moment().format('MM/DD/YYYY'),
        };
        break;
      }

      case 'futureFlights': {
        data = {
          ...data,
          flightDateStart: Moment().add(1, 'day').format('MM/DD/YYYY'),
          flightDateEnd: Moment().add(730, 'days').format('MM/DD/YYYY'),
        };
        break;
      }
    }
  }

  if (data.filters) {
    data.filters.forEach((filter) => {
      if (filter.name === 'flightDate') {
        data.flightDateStart = Moment(filter.value[0]).format('YYYY-MM-DD');
        data.flightDateEnd = Moment(filter.value[1]).format('YYYY-MM-DD');
      } else {
        data[filter.name] = filter.value;
      }
    });
  }

  switch (currentTab) {
    case RequestListTab.Passenger: {
      data.category = 'Passenger';
      break;
    }

    case RequestListTab.Construction: {
      data.category = 'Construction';
      break;
    }

    case RequestListTab.Helicopter: {
      data.category = 'Helicopter';
      break;
    }

    case RequestListTab.Drone: {
      data.category = 'Drone';
      break;
    }

    default: {
      break;
    }
  }

  return data;
};

export const getTimeOptions = (
  hasHalftime = false,
  hasQuartertime = true,
  start = '05:00',
  totalHours = 16
) => {
  let timesCounter = totalHours;
  let multiplier = 60;

  if (hasHalftime) {
    timesCounter = timesCounter * 2 - 1;
    multiplier = 30;
  } else if (hasQuartertime) {
    timesCounter = timesCounter * 4 - 1;
    multiplier = 15;
  }

  return Array(timesCounter)
    .fill(0)
    .map((_, i) => {
      const date = Moment(start, 'HH:mm').add(i * multiplier, 'minute');

      return {
        key: date.format('HH:mm'),
        value: date.format('HH:mm'),
        text: `${date.format('HHmm')} hrs`,
      };
    });
};

export const getRequestUrl = (
  data: PartialDeep<IRequestForm>,
  params?: Record<string, any>
) => {
  return getUrl(
    {
      requestId: data.request?.requestId,
      category: RequestFlightPathName[data.request?.flightFlag ?? 1],
      ...(params ?? {}),
    },
    '/flight'
  );
};

export const formatRequestData = (data?: IRequest) => {
  if (!data) {
    return data;
  }

  const value = {
    ...data,
  };

  if (value.request?.flightDate) {
    value.request.flightDate = Moment.utc(value.request?.flightDate).toDate();
  }

  if (value.request?.requestorPhone) {
    value.request.requestorPhone = formatPhoneNumber(
      value.request.requestorPhone
    );
  }

  if (value.request?.fieldPhone) {
    value.request.fieldPhone = formatPhoneNumber(value.request.fieldPhone);
  }

  if (value.constructionContractorFlight?.pilotPhone) {
    value.constructionContractorFlight.pilotPhone = formatPhoneNumber(
      value.constructionContractorFlight.pilotPhone
    );
  }

  if (value.constructionContractorFlight?.primePhone) {
    value.constructionContractorFlight.primePhone = formatPhoneNumber(
      value.constructionContractorFlight.primePhone
    );
  }

  if (value.passengerContractorFlight?.pilotPhone) {
    value.passengerContractorFlight.pilotPhone = formatPhoneNumber(
      value.passengerContractorFlight.pilotPhone
    );
  }

  if (value.passengerContractorFlight?.primePhone) {
    value.passengerContractorFlight.primePhone = formatPhoneNumber(
      value.passengerContractorFlight.primePhone
    );
  }

  return value;
};

const unsetDataValue = (data, value, list?: string[]) => {
  const newData = {
    ...data,
  };

  if (list) {
    list.forEach((name) => {
      if (data[name] && data[name].length) {
        data[name].forEach((_, index) => {
          unset(newData, `${name}[${index}].${value}`);
        });
      }
    });
  } else {
    unset(newData, value);
  }

  return newData;
};

export const getCleanData = (data: IRequest) => {
  let newData = {
    ...data,
  };

  if (data.request.flightFlag === FlightCategory.ConstructionWork) {
    if (
      data.constructionFlight.landingInfos &&
      data.constructionFlight.landingInfos.length
    ) {
      data.constructionFlight.landingInfos.forEach(
        ({ landingId, ...values }, index) => {
          newData = set(
            newData,
            `constructionFlight.landingInfos[${index}]`,
            values
          );

          unset(
            newData,
            `constructionFlight.landingInfos[${index}].file.uploadId`
          );
        }
      );
    }

    if (
      data.constructionFlight.structureInfos &&
      data.constructionFlight.structureInfos.length
    ) {
      data.constructionFlight.structureInfos.forEach(
        ({ structureKey, ...values }, index) => {
          newData = set(
            newData,
            `constructionFlight.structureInfos[${index}]`,
            values
          );
        }
      );
    }

    newData = {
      ...newData,
      constructionFlight: {
        ...newData.constructionFlight,
        ...unsetDataValue(newData.constructionFlight, 'uploadId', [
          'culturalFiles',
          'sensitiveFiles',
          'structureFiles',
        ]),
      },
    };
  } else if (
    data.request.flightFlag === FlightCategory.ContractorConstruction
  ) {
    if (
      data.constructionContractorFlight.landingInfos &&
      data.constructionContractorFlight.landingInfos.length
    ) {
      data.constructionContractorFlight.landingInfos.forEach(
        ({ landingId, ...values }, index) => {
          newData = set(
            newData,
            `constructionContractorFlight.landingInfos[${index}]`,
            values
          );

          unset(
            newData,
            `constructionContractorFlight.landingInfos[${index}].file.uploadId`
          );
        }
      );
    }

    if (
      data.constructionContractorFlight.structureInfos &&
      data.constructionContractorFlight.structureInfos.length
    ) {
      data.constructionContractorFlight.structureInfos.forEach(
        ({ structureKey, ...values }, index) => {
          newData = set(
            newData,
            `constructionContractorFlight.structureInfos[${index}]`,
            values
          );
        }
      );
    }

    newData = {
      ...newData,
      constructionContractorFlight: {
        ...newData.constructionContractorFlight,
        ...unsetDataValue(newData.constructionContractorFlight, 'uploadId', [
          'structureFiles',
          'culturalFiles',
          'sensitiveFiles',
          'liftFiles',
          'airOpsFiles',
          'hecFiles',
          'congestedFiles',
        ]),
      },
    };
  }

  return newData;
};

export const getCSVContent = (
  data: (string | null | number | undefined)[][],
  title?: string[]
) => {
  let table = '<table border="1" cellspacing="1" cellpadding="2">';
  if (title?.length) {
    table += '<tr>';
    title.forEach((hdr) => {
      table += `<th bgcolor="000080" nowrap><font color="FFFFFF">${hdr}</font></th>`;
    });
    table += '</tr>';
  }
  if (data?.length) {
    data.forEach((row, index) => {
      const tdColor = index % 2 === 0 ? '#e8ffff' : '#fff';
      table += '<tr>';
      row.forEach((value) => {
        table += `<td bgcolor="${tdColor}"><font color="#000">${value}</font></td>`;
      });
      table += '</tr>';
    });
  }

  table += '</table>';
  return `data:application/vnd.ms-excel, ${encodeURIComponent(table)}`;
};

export const getChangeLogDetails = (changeLog: IChangeLog[]) => {
  return changeLog.filter((logItem) => logItem.ChangeLogDetail !== null);
};
